import type { Dispatch, SetStateAction } from "react"

import { FILTER_OPERATOR_OPTIONS, type FilterFormState } from "./editorUtils"
import { Button } from "shared/ui/button"
import { Checkbox } from "shared/ui/checkbox"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Textarea } from "shared/ui/textarea"

interface FilterDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  editingFilterIndex: number | null
  filterForm: FilterFormState
  setFilterForm: Dispatch<SetStateAction<FilterFormState>>
  fieldKeyOptions: string[]
  onApply: () => void
}

export function FilterDialog({
  open,
  onOpenChange,
  editingFilterIndex,
  filterForm,
  setFilterForm,
  fieldKeyOptions,
  onApply,
}: FilterDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{editingFilterIndex === null ? "Add Report Filter" : "Edit Report Filter"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>Field Key *</Label>
            {fieldKeyOptions.length > 0 ? (
              <Select
                value={filterForm.fieldKey}
                onValueChange={(value) => setFilterForm((prev) => ({ ...prev, fieldKey: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select field key" />
                </SelectTrigger>
                <SelectContent>
                  {fieldKeyOptions.map((item) => (
                    <SelectItem key={item} value={item}>
                      {item}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={filterForm.fieldKey}
                onChange={(event) => setFilterForm((prev) => ({ ...prev, fieldKey: event.target.value }))}
                placeholder="field_key"
              />
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Operator *</Label>
              <Select
                value={filterForm.operator}
                onValueChange={(value) => setFilterForm((prev) => ({ ...prev, operator: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FILTER_OPERATOR_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Index</Label>
              <Input
                type="number"
                min={1}
                value={filterForm.orderIndex}
                onChange={(event) =>
                  setFilterForm((prev) => ({ ...prev, orderIndex: Number.parseInt(event.target.value || "1", 10) || 1 }))
                }
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Default Value JSON</Label>
            <Textarea
              rows={4}
              value={filterForm.defaultValueJson}
              onChange={(event) => setFilterForm((prev) => ({ ...prev, defaultValueJson: event.target.value }))}
              placeholder='{"from":"2026-01-01","to":"2026-01-31"}'
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="filter-required"
              checked={filterForm.required}
              onCheckedChange={(checked) => setFilterForm((prev) => ({ ...prev, required: Boolean(checked) }))}
            />
            <Label htmlFor="filter-required">Required filter</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onApply}>Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
